import { useFormik } from 'formik'
import React, { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { useEdition } from 'hooks/useEdition'
import { useVocabularies } from 'hooks/useVocabularies'
import { get } from 'utils/objectUtils'

import { requiredError } from 'Components/Forms/FormErrors'
import { ENS_TYPES, ENS_TYPES_OBJ, getVocabularies } from 'utils/entityUtils'
import EditComponent from '../Fields/EditComponent'
import { InputAcceptRegulation } from '../Fields/InputAcceptRegulation'

import { Box } from '@mui/system'
import { ArrowDown } from 'Components/CustomIcons/ArrowDown'
import { ArrowUp } from 'Components/CustomIcons/ArrowUp'
import { AlertComponent } from 'Components/HelperComponents'
import { ProposalEntitiesList } from 'NewVersion/components/Proposal/ProposalEntitiesList'
import 'NewVersion/styles/Form.scss'
import { ReactComponent as PdfLogo } from 'Statics/pdf.svg'
import { useGuillotina } from 'hooks/useGuillotina'
import { useScrollToFirstError } from 'hooks/useScrollToFirstError'
import Button from '../UI/Buttons/Button'

const OEDER_FIELDS_SHOP_FORM = [
  {
    title: 'data_shop_form',
    fields: ['title', 'tipus', 'descompte_artesa', 'descripcio_activitat'],
  },
  {
    title: 'docs_shop_form',
    fields: ['fitxer_dni', 'fitxer_asseguranca', 'fitxer_seguretat_social', 'imatge'],
  },
]

const REQUIERED_FIELDS_SHOP_FORM = [
  'title',
  'tipus',
  'fitxer_dni',
  'fitxer_asseguranca',
  'fitxer_seguretat_social',
  'imatge',
  'descripcio_activitat',
]

export const EntityForm = ({ entity, ensTypeObj, handleSubmit }) => {
  const entityVocabularies = useVocabularies(getVocabularies(ensTypeObj))
  const { loading } = useEdition()

  const {
    data: entitySchemaData,
    isLoading: entitySchemaLoading,
    error: entitySchemaError,
  } = useGuillotina({
    path: `@types/${ensTypeObj['interface']}`,
  })
  const { data: schemaShop, isLoading: schemaLoading } = useGuillotina({ path: '@types/Parada' })

  const { loading: loadingVocabularies, data: vocabularies } = useVocabularies([
    {
      vocabulary: 'tipus_parada',
    },
    {
      vocabulary: 'trams_parada',
    },
  ])

  if (
    loading ||
    loadingVocabularies ||
    !entityVocabularies.data ||
    entityVocabularies.loading ||
    schemaLoading ||
    entitySchemaLoading
  ) {
    return <LoadingComponent />
  }

  return (
    <EntityFormContent
      entityVocabularies={entityVocabularies}
      vocabularies={vocabularies}
      entitySchema={{
        data: entitySchemaData,
        error: entitySchemaError,
        loading: entitySchemaLoading,
      }}
      handleSubmit={handleSubmit}
      ensTypeObj={ensTypeObj}
      entity={entity}
      schemaShop={schemaShop}
      addParades={ensTypeObj.interface === ENS_TYPES.service}
    />
  )
}

const EntityFormContent = ({
  entity,
  entitySchema,
  ensTypeObj,
  readOnlyForm = false,
  handleSubmit,
  schemaShop,
  vocabularies,
  addParades,
}) => {
  const [openInfo, setOpenInfo] = useState(false)
  const { currentEdition } = useEdition()
  const { t } = useTranslation()

  const isManager = useMemo(() => {
    return entity && entity.tipus === ENS_TYPES_OBJ.representant.text
  }, [entity])

  const isArtist = useMemo(() => {
    return entity && entity.tipus === ENS_TYPES_OBJ.artista.text
  }, [entity])

  const {
    data: artistsData,
    isLoading: artistsLoading,
    error: artistsError,
  } = useGuillotina({
    path: isManager && entity ? `${entity['@id']}/@artistes` : null,
  })

  const {
    data: managerData,
    isLoading: managerLoading,
    error: managerError,
  } = useGuillotina({
    path: isArtist && entity && entity.representant ? entity.representant : null,
  })

  const getRates = (value) => {
    const result = get(currentEdition.getConfigData(), `tarifes_parades.${value}.estandard`, null)
    if (!result) {
      return get(currentEdition.getConfigData(), 'preu_defecte_parades', '--')
    }
    return result
  }

  const renderShopForm = () => !entity && addParades

  const getInitialValues = React.useMemo(() => {
    const allFields = []
    const result = {}

    if (entity)
      ensTypeObj.orderFieldsForm.forEach((item) => {
        if (item.fields && Array.isArray(item.fields)) {
          allFields.push(...item.fields)
        }
      })

    allFields.map((field) => (result[field] = entity[field]))

    if (renderShopForm()) {
      REQUIERED_FIELDS_SHOP_FORM.forEach((key) => (result[key] = null))
    }

    return result
  }, [ensTypeObj])

  const formik = useFormik({
    initialValues: getInitialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validate: (values) => {
      const errors = {}
      ensTypeObj['requiredFields'].forEach((key) => {
        if (key !== 'declaracio_acceptar') {
          const keyForm = `${key}`
          let value = get(values, keyForm, null)
          if (typeof value === 'string') {
            value = value.trim()
          }
          if ((!(keyForm in values) || !value) && renderLocationFields(key)) {
            errors[keyForm] = requiredError()
          }
        }
      })

      const keyForm = `entity_correu`
      if (values[keyForm] && !values[keyForm].match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)) {
        errors[keyForm] = t('invalid_email')
      }

      if (!('declaracio_acceptar' in values) || !values['declaracio_acceptar']) {
        errors['declaracio_acceptar'] = requiredError()
      }

      if (renderShopForm()) {
        REQUIERED_FIELDS_SHOP_FORM.forEach((key) => {
          const keyForm = `shop_${key}`
          let value = get(values, keyForm, null)
          if (typeof value === 'string') {
            value = value.trim()
          }
          if (!(keyForm in values) || !value) {
            errors[keyForm] = requiredError()
          }
        })
        if (
          values[`shop_correu`] &&
          !values[`shop_correu`].match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)
        ) {
          errors['shop_correu'] = t('invalid_email')
        }
      }

      console.log(errors)
      return errors
    },
  })

  useScrollToFirstError(formik.errors, formik.submitCount)

  const fieldsWithPrefixLabel = ['idioma', 'correu', 'telefon', 'mobil']
  const fieldsWithPrefixLabelShop = ['title', 'tipus', 'imatge']

  const getLabel = (key, fieldsPrefix, prefix = 'ens_') => {
    if (fieldsPrefix.includes(key)) return `${prefix}${key}`
    return key
  }

  const renderInputAcceptRegulation = (key) => {
    return (
      <>
        {renderShopForm() && (
          <div>
            <PdfLogo />
            <a href="/Acord_FiraPasseig.pdf" download>
              {t('acords_participacio')}
            </a>
          </div>
        )}
        <InputAcceptRegulation
          onChange={(e) => {
            formik.setFieldValue(key, e.target.checked, true)
          }}
          onBlur={formik.onBlur}
          checked={!!formik.values[key]}
          error={formik.touched[key] && formik.errors[key]}
          readOnly={readOnlyForm}
          name={key}
        />
      </>
    )
  }

  const renderLocationFields = (key) => {
    if (key === 'comunitat_autonoma' && get(formik, 'values.pais', '') !== 'country-ES') {
      return false
    }
    if (
      key === 'comarca' &&
      (get(formik, 'values.pais', '') !== 'country-ES' ||
        get(formik, 'values.comunitat_autonoma', '') !== 'catalunya')
    ) {
      return false
    }
    return true
  }

  const renderFields = (key) => {
    if (key === 'declaracio_acceptar') return false
    return renderLocationFields(key)
  }

  const getDescription = (value, key) => {
    if (key === 'correu') {
      return t('ens_correu_description')
    } else if (key === 'web') {
      return t('web_description')
    } else if (key === 'facebook') {
      return t('facebook_description')
    } else if (key === 'twitter') {
      return t('twitter_description')
    } else if (key === 'instagram') {
      return t('instagram_description')
    } else if (key === 'linkedin') {
      return t('linkedin_description')
    } else if (key === 'youtube') {
      return t('youtube_description')
    }
    return value.description ? t(`${key}_description`) : null
  }

  const renderFiraPasseig = () => {
    return (
      <>
        <h4 className="header-add-accreditations">{t('add_shop_title')}</h4>
        <Box mt={2}>
          <AlertComponent type="info" className="info-tarifes">
            <div
              onClick={() => setOpenInfo(!openInfo)}
              className={`header ${openInfo ? 'open' : ''}`}
            >
              <strong>{t('rates_information')}</strong>
              {openInfo ? (
                <ArrowUp width={25} height={25} fill="black" />
              ) : (
                <ArrowDown width={25} height={25} fill="black" />
              )}
            </div>

            {openInfo && (
              <article>
                {vocabularies['trams_parada'].map((tram, idx) => {
                  if (currentEdition.getConfigData()['hi_ha_dos_trams'] || idx === 0) {
                    return (
                      <Fragment key={tram.value}>
                        <div>
                          {t('rates')}
                          {currentEdition.getConfigData()['hi_ha_dos_trams'] &&
                            ` - ${t(tram.value)}`}
                        </div>
                        <ul>
                          {vocabularies.tipus_parada.map((item) => {
                            return (
                              <li key={item.value}>
                                {t(item.value)} -- {getRates(`${tram.value}.${item.value}`)}€
                              </li>
                            )
                          })}
                        </ul>
                      </Fragment>
                    )
                  }
                  return null
                })}
              </article>
            )}
          </AlertComponent>
        </Box>
        <div key={`shopForm`} className="acreditacio">
          <div>
            {OEDER_FIELDS_SHOP_FORM.map((item) => {
              return (
                <Fragment key={`shopFormSection_${item['title']}`}>
                  {item['title'] && (
                    <h5 className="header-add-accreditations">{`${t(item['title'])}`}</h5>
                  )}
                  <div className="fragment">
                    {item['fields'].map((key) => {
                      if (get(schemaShop, `properties[${key}]`, null)) {
                        const valueSchema = schemaShop.properties[key]
                        const keyForm = `shop_${key}`
                        return (
                          <EditComponent
                            key={keyForm}
                            name={keyForm}
                            label={t(getLabel(key, fieldsWithPrefixLabelShop, 'shop_'))}
                            schema={valueSchema}
                            description={
                              valueSchema.description
                                ? t(
                                    `${getLabel(
                                      key,
                                      fieldsWithPrefixLabelShop,
                                      'shop_'
                                    )}_description`
                                  )
                                : null
                            }
                            value={formik.values[keyForm]}
                            required={REQUIERED_FIELDS_SHOP_FORM.includes(key)}
                            onChange={(ev) => {
                              formik.setFieldValue(keyForm, ev, true)
                            }}
                            error={formik.errors[keyForm]}
                            dataTest={`inputFieldTest_${keyForm}`}
                          />
                        )
                      }
                    })}
                  </div>
                </Fragment>
              )
            })}
          </div>
        </div>
      </>
    )
  }

  const renderField = (key) => {
    if (renderFields(key) && get(entitySchema, `data.properties[${key}]`, null)) {
      const value = entitySchema.data.properties[key]
      return (
        <EditComponent
          key={key}
          name={key}
          label={
            key === 'title'
              ? t(getLabel(ensTypeObj['labelName'], fieldsWithPrefixLabel))
              : t(getLabel(key, fieldsWithPrefixLabel))
          }
          schema={value}
          description={getDescription(value, key)}
          value={formik.values[key]}
          required={(ensTypeObj['requiredFields'] ?? []).includes(key)}
          onChange={(ev) => {
            formik.setFieldValue(key, ev, true)
          }}
          error={formik.errors[key]}
          dataTest={`inputFieldTest_${key}`}
        />
      )
    }
  }

  const renderColumns = (item) => {
    const middleIndex = Math.ceil(item['fields'].length / 2)
    return (
      <div className="fragment">
        <div className="fragmentColumn">
          {item['fields'].slice(0, middleIndex).map((key) => renderField(key))}
        </div>
        <div className="fragmentColumn">
          {item['fields'].slice(middleIndex).map((key) => renderField(key))}
        </div>
      </div>
    )
  }

  const renderManagerInfo = () => {
    if (!isArtist || !entity || !entity.representant) return null

    const transformedManager = useMemo(() => {
      if (!managerData) return null

      // Extract or construct the path
      let path = managerData.path

      // If path doesn't exist, try to construct it from @id
      if (!path && managerData['@id']) {
        // @id format: "https://dbfm.iskra.cat/testing/container_test/2026/foo_manager"
        // Extract /year/entity_id pattern
        const match = managerData['@id'].match(/\/\d{4}\/[^/]+$/)
        if (match) {
          path = match[0] // This will be "/2026/foo_manager"
        }
      }

      // If still no path, construct from @name and parse year from @id
      if (!path && managerData['@name']) {
        const yearMatch = managerData['@id']?.match(/\/(\d{4})\//)
        const year = yearMatch ? yearMatch[1] : currentEdition.edition
        path = `/${year}/${managerData['@name']}`
      }

      return {
        ...managerData,
        path: path,
        year: managerData.parent?.title || managerData.year || currentEdition.edition || '',
      }
    }, [managerData, currentEdition])

    return (
      <>
        <h5 style={{ margin: '0px' }}>{t('entity_representant')}&nbsp;</h5>
        <div className="proposals" style={{ marginBottom: '60px' }}>
          {managerError && (
            <AlertComponent type="warning">{t('error-fetching-data')}</AlertComponent>
          )}
          <div id="EntityItem">
            {managerLoading && <LoadingComponent />}
            {transformedManager && (
              <div className="entity-container">
                <ProposalEntitiesList entities={[transformedManager]} />
              </div>
            )}
          </div>
        </div>
      </>
    )
  }

  const renderArtistsList = () => {
    if (!isManager || !entity) return null

    const transformedArtists = useMemo(() => {
      if (!artistsData || !Array.isArray(artistsData)) return []

      return artistsData.map((artist) => {
        // Extract or construct the path
        let path = artist.path

        // If path doesn't exist, try to construct it from @id
        if (!path && artist['@id']) {
          // @id format: "https://dbfm.iskra.cat/testing/container_test/2026/foo_artista"
          // Extract /year/entity_id pattern
          const match = artist['@id'].match(/\/\d{4}\/[^/]+$/)
          if (match) {
            path = match[0] // This will be "/2026/foo_artista"
          }
        }

        // If still no path, construct from @name and parse year from @id
        if (!path && artist['@name']) {
          const yearMatch = artist['@id']?.match(/\/(\d{4})\//)
          const year = yearMatch ? yearMatch[1] : currentEdition.edition
          path = `/${year}/${artist['@name']}`
        }

        return {
          ...artist,
          path: path,
          year: artist.parent?.title || artist.year || currentEdition.edition || '',
        }
      })
    }, [artistsData, currentEdition])

    return (
      <>
        <h5 style={{ margin: '0px' }}>{t('entity_artistes')}&nbsp;</h5>
        <div className="proposals" style={{ marginBottom: '60px' }}>
          {artistsError && (
            <AlertComponent type="warning">{t('error-fetching-data')}</AlertComponent>
          )}
          <div id="EntityItem">
            {artistsLoading && <LoadingComponent />}
            <div className="entity-container">
              <ProposalEntitiesList entities={transformedArtists} />
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <form
        className="Form"
        onSubmit={(values) => {
          formik.handleSubmit(values)
        }}
        data-test="formTest"
      >
        {entitySchema &&
          entitySchema.data &&
          !entitySchema.loading &&
          ensTypeObj['orderFieldsForm']
            .filter((item) => item.title !== 'declaracio_fieldset')
            .map((item) => {
              return (
                <Fragment key={item['title']}>
                  <h5>{t(item['title'])}&nbsp;</h5>
                  {item['subtitle'] && item['subtitle'] !== '' && <p>{t(item['subtitle'])}</p>}
                  {renderColumns(item)}
                </Fragment>
              )
            })}
        {renderShopForm() && renderFiraPasseig()}
        <Box mt={5} mb={3}>
          <h5>{t('declaracio_fieldset')}&nbsp;</h5>
          {renderInputAcceptRegulation('declaracio_acceptar')}
        </Box>
        <div>
          {formik.isSubmitting && <LoadingComponent />}
          {!readOnlyForm && !formik.isSubmitting && (
            <div className="footer-buttons">
              <Button type="submit" data-test="btnSubmitFormTest" disabled={formik.isSubmitting}>
                {entity ? t('save') : t('next')}
              </Button>
            </div>
          )}
        </div>
      </form>

      {renderManagerInfo()}
      {renderArtistsList()}
    </>
  )
}

export default EntityForm
